'use client';
/**
 * ./src/modules/product/components/samples/SamplesFilterBar.tsx
 *
 * SamplesFilterBar component to display the Samples filter bar
 *
 * @since 05/27/2024
 * @copyright DesignShop, 2024
 */
import * as React from 'react';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import { BREAKPOINT_SM } from '@/modules/common/constants';
import useIsMobile from '@/modules/common/hooks/useIsMobile';
import { useAppDispatch, useAppSelector } from '@/modules/common/hooks/useStore';
import { closeDataLayerEvent } from '@/modules/common/services/data-layer-events/close';
import { openDataLayerEvent } from '@/modules/common/services/data-layer-events/open';
import { useListingContextSafe } from '@/modules/product/context/ListingContext';

import { FilterItemApplied } from '../../models/filter-item.interface';
import { selectFiltersOpen, toggleFilters } from '../../state/filterSlice';
import { IconFilters } from '../icons';
import FiltersModal, { FiltersModalTrigger } from '../modal/FiltersModal';
import { SamplesFilterIndicator } from './SamplesFilterIndicator';

const mobileFilterActionsWidth = 48;

interface SamplesFilterBarProps {
  id?: string;
  pageTitle?: string;
  className?: string;
  subTitle?: string;
  hasNoHits?: boolean;
  where?: DataLayerWhere;
  /** When true (e.g. simplified search page), use URL-based filters and pass data to FiltersModal */
  useSimplifiedFilters?: boolean;
  currentItems?: FilterItemApplied[];
  facets?: Record<string, Record<string, number>>;
  /** When true, hide the desktop Filters button (e.g. when Filters button is in left sidebar) */
  hideFiltersButtonOnDesktop?: boolean;
  /** When true, hide the bottom border (e.g. for simplified search page layout) */
  hideBorder?: boolean;
  actionsMountId?: string;
}

export const SamplesFilterBar = ({
  id = 'filter-bar',
  pageTitle,
  className,
  subTitle,
  hasNoHits,
  where,
  useSimplifiedFilters = false,
  currentItems: currentItemsProp,
  facets: facetsProp,
  hideFiltersButtonOnDesktop = false,
  hideBorder = false,
  actionsMountId,
}: SamplesFilterBarProps) => {
  const dispatch = useAppDispatch();
  const filtersOpen = useAppSelector(selectFiltersOpen);
  const isMobile = useIsMobile(BREAKPOINT_SM);

  const listing = useListingContextSafe();
  const contextFilters = listing?.current?.filters ?? [];
  const filters = useSimplifiedFilters && currentItemsProp ? currentItemsProp : contextFilters;
  const appliedFilters = filters.length ?? 0;

  const handleFiltersToggle = () => {
    const feature = 'Filter panel';
    dispatch(toggleFilters());
    if (!filtersOpen) {
      openDataLayerEvent({ feature, where });
    } else if (filtersOpen) {
      closeDataLayerEvent({ feature, where });
    }
  };

  return (
    <div id={id} className={className} data-testid="filter-bar">
      <div
        className={`app-container bg-white ${hideBorder ? 'py-1 pb-0' : 'border-b border-gray-75 pt-[14px] md:pb-4 md:pt-8'}`}
        data-testid="filter-bar-container"
      >
        <div
          className={`flex ${hideBorder ? 'items-center' : 'items-end'} justify-between gap-3 md:px-3 lg:px-0`}
        >
          <div className={hideBorder ? '' : 'md:py-0.5'}>
            {subTitle && (
              <p
                className={`text-[13px] leading-4 ${hideBorder ? '' : 'mb-2'}`}
                data-testid="sub-title"
              >
                {subTitle}
              </p>
            )}

            {pageTitle && (
              <h1
                className="pb-4 text-2xl font-semibold leading-6 text-gray-900 md:pb-0 md:leading-8"
                data-testid="page-title"
              >
                {pageTitle}
              </h1>
            )}
          </div>

          {!hasNoHits && (
            <div
              className={`flex flex-1 items-center justify-end gap-1 ${hideBorder ? 'pb-0' : 'pb-2'} md:ml-3 md:flex-none md:pb-0 w-${mobileFilterActionsWidth}px md:w-auto`}
              data-testid="filter-actions"
            >
              {actionsMountId ?
                <div id={actionsMountId} className="flex w-full items-center justify-end gap-1" />
              : isMobile ?
                <>
                  <div className="ml-1 h-4 w-[1px] bg-[#E4E4E4]"></div>
                  <div className="flex items-center" data-testid="filter-modal">
                    <FiltersModalTrigger
                      where={where}
                      useSimplifiedFilters={useSimplifiedFilters}
                      currentItems={currentItemsProp}
                      facets={facetsProp}
                    />
                    <FiltersModal where={where} />
                  </div>
                </>
              : !hideFiltersButtonOnDesktop ?
                <Button
                  variant="ghost"
                  size={'m'}
                  aria-haspopup="true"
                  aria-expanded={filtersOpen}
                  className="items-center px-2 py-0.5"
                  onClick={handleFiltersToggle}
                  data-testid="filter-button"
                >
                  <div className="relative" data-testid="filter-icon">
                    <IconFilters />
                    {appliedFilters > 0 && (
                      <SamplesFilterIndicator
                        filtersCount={appliedFilters}
                        className="absolute right-[1px] top-[2px] outline outline-1 outline-white"
                        data-testid="filter-indicator"
                      />
                    )}
                  </div>
                  Filters
                </Button>
              : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export function SamplesFilterBarActions({ where }: { where?: DataLayerWhere }) {
  const dispatch = useAppDispatch();
  const filtersOpen = useAppSelector(selectFiltersOpen);
  const isMobile = useIsMobile(BREAKPOINT_SM);

  const listing = useListingContextSafe();
  const appliedFilters = listing?.current?.filters?.length ?? 0;

  const handleFiltersToggle = () => {
    const feature = 'Filter panel';
    dispatch(toggleFilters());
    if (!filtersOpen) {
      openDataLayerEvent({ feature, where });
    } else if (filtersOpen) {
      closeDataLayerEvent({ feature, where });
    }
  };

  if (isMobile) {
    return (
      <>
        <div className="ml-1 h-4 w-[1px] bg-[#E4E4E4]"></div>
        <div data-testid="filter-modal">
          <FiltersModalTrigger where={where} />
          <FiltersModal where={where} />
        </div>
      </>
    );
  }

  return (
    <Button
      variant="ghost"
      size={'m'}
      aria-haspopup="true"
      aria-expanded={filtersOpen}
      className="items-center px-2 py-0.5"
      onClick={handleFiltersToggle}
      data-testid="filter-button"
    >
      <div className="relative" data-testid="filter-icon">
        <IconFilters />
        {appliedFilters > 0 && (
          <SamplesFilterIndicator
            filtersCount={appliedFilters}
            className="absolute right-[1px] top-[2px] outline outline-1 outline-white"
            data-testid="filter-indicator"
          />
        )}
      </div>
      Filters
    </Button>
  );
}

export default SamplesFilterBar;
